'use client'

import { useId } from 'react'
import { cn } from '@/lib/utils'

/**
 * The illuminated world scope for the GPU lab. Continents are rough
 * outlines in longitude/latitude, drawn as one dotted path; each offered
 * region is a pin at its city. Pins are a shortcut for the region keys next
 * to the map (the keys are the keyboard path), so they stay out of the tab
 * order.
 */

// Equirectangular, cropped to latitudes 80N..60S.
const TOP = 80
const SPAN_Y = 140
const project = ([lon, lat]: [number, number]) => [lon + 180, TOP - lat] as const

const CONTINENTS: [number, number][][] = [
  // North America
  [[-165, 64], [-150, 70], [-125, 70], [-95, 72], [-80, 68], [-62, 58], [-55, 50], [-66, 44], [-76, 36], [-81, 27], [-80, 25], [-88, 30], [-97, 26], [-97, 19], [-88, 16], [-83, 9], [-78, 8], [-92, 15], [-105, 20], [-112, 28], [-118, 33], [-124, 41], [-125, 49], [-135, 57], [-150, 60], [-165, 58]],
  // Greenland
  [[-55, 60], [-42, 60], [-20, 70], [-22, 81], [-60, 80], [-70, 76]],
  // South America
  [[-78, 8], [-62, 10], [-50, 0], [-35, -6], [-40, -22], [-48, -27], [-58, -38], [-66, -45], [-68, -55], [-74, -50], [-73, -38], [-70, -18], [-81, -5], [-80, 2]],
  // Europe and Asia
  [[-9, 37], [-9, 43], [-2, 44], [-4, 48], [5, 52], [9, 57], [5, 61], [15, 69], [28, 71], [42, 67], [60, 70], [80, 73], [105, 77], [140, 72], [180, 68], [178, 63], [160, 60], [155, 57], [142, 54], [140, 47], [132, 43], [129, 35], [122, 31], [120, 24], [110, 20], [108, 12], [104, 9], [100, 13], [98, 8], [103, 1], [98, 10], [94, 17], [91, 22], [86, 20], [80, 15], [78, 8], [73, 17], [70, 22], [66, 25], [57, 25], [50, 30], [48, 29], [56, 24], [59, 22], [52, 16], [44, 12], [39, 22], [34, 28], [35, 33], [36, 36], [28, 36], [26, 40], [22, 37], [19, 41], [14, 45], [16, 40], [12, 38], [8, 44], [3, 43], [-2, 37]],
  // Great Britain and Ireland
  [[-5, 50], [1, 51], [0, 53], [-2, 56], [-3, 58.5], [-6, 58], [-5, 55], [-3, 54]],
  [[-10, 51.5], [-6, 52], [-6, 55], [-8, 55.3], [-10, 54]],
  // Japan
  [[130, 31], [135, 34], [140, 35], [142, 39], [141, 45], [139, 41], [136, 37], [131, 34]],
  // Africa
  [[-17, 21], [-16, 28], [-10, 33], [-6, 36], [10, 37], [20, 32], [32, 31], [35, 28], [43, 12], [51, 12], [48, 4], [40, -10], [40, -16], [35, -24], [32, -29], [27, -34], [20, -35], [17, -30], [12, -18], [13, -10], [9, -1], [9, 4], [-8, 4], [-13, 8], [-17, 14]],
  // Madagascar
  [[44, -16], [50, -14], [48, -25], [44, -24]],
  // Indonesia and New Guinea
  [[95, 5], [105, -6], [115, -8], [124, -9], [118, -4], [110, -2], [100, 2]],
  [[131, -1], [141, -3], [150, -10], [141, -9], [134, -4]],
  // Australia
  [[114, -22], [122, -18], [130, -12], [136, -12], [142, -11], [145, -15], [153, -26], [150, -37], [146, -39], [140, -38], [135, -34], [130, -31], [115, -34]],
  // New Zealand
  [[172, -34], [178, -38], [174, -41], [168, -46], [167, -45], [173, -41]],
]

const LAND = CONTINENTS.map(
  (shape) =>
    'M' +
    shape
      .map((point) => {
        const [x, y] = project(point)
        return `${x.toFixed(1)} ${y.toFixed(1)}`
      })
      .join(' L') +
    ' Z'
).join(' ')

const GRATICULE = [
  ...Array.from({ length: 11 }, (_, i) => `M${(i + 1) * 30} 0 V${SPAN_Y}`),
  ...Array.from({ length: 4 }, (_, i) => `M0 ${(i + 1) * 30 - 10} H360`),
].join(' ')

/** Where each offered region's city sits, [longitude, latitude]. */
const REGION_COORDS: Record<string, [number, number]> = {
  'us-east-1': [-77.5, 39],
  'us-east-2': [-83, 40],
  'us-west-1': [-122, 37.5],
  'us-west-2': [-120.5, 45.5],
  'ca-central-1': [-73.6, 45.5],
  'eu-west-1': [-6.3, 53.3],
  'eu-west-2': [-0.1, 51.5],
  'eu-central-1': [8.7, 50.1],
  'eu-north-1': [18, 59.3],
  'ap-northeast-1': [139.7, 35.7],
  'ap-northeast-2': [127, 37.5],
  'ap-southeast-1': [103.8, 1.4],
  'ap-southeast-2': [151.2, -33.9],
  'ap-south-1': [72.9, 19],
  'ap-east-1': [114.2, 22.3],
  'af-south-1': [18.4, -33.9],
}

interface RegionMapProps {
  regions: { id: string; label: string }[]
  /** Regions the selected GPU can be created in. */
  offered: Set<string>
  selectedId: string
  on: boolean
  onSelect: (id: string) => void
  className?: string
}

export function RegionMap({ regions, offered, selectedId, on, onSelect, className }: RegionMapProps) {
  const dots = useId().replace(/:/g, '')

  return (
    <div className={cn('lab-scope relative aspect-[360/140] w-full overflow-hidden', className)}>
      <svg viewBox={`0 0 360 ${SPAN_Y}`} aria-hidden className="absolute inset-0 h-full w-full">
        <defs>
          <pattern id={dots} width="2.6" height="2.6" patternUnits="userSpaceOnUse">
            <circle cx="1.3" cy="1.3" r="0.75" className="fill-background/35" />
          </pattern>
        </defs>
        <path d={GRATICULE} fill="none" strokeWidth="0.3" className="stroke-accent/15" />
        <path d={LAND} fill={`url(#${dots})`} />
        <path d={LAND} fill="none" strokeWidth="0.35" className="stroke-background/20" />
      </svg>
      <div aria-hidden className="lab-sweep pointer-events-none absolute inset-0" />

      {regions.map((region) => {
        const coords = REGION_COORDS[region.id]
        if (!coords) return null
        const [x, y] = project(coords)
        const lit = on && offered.has(region.id)
        const selected = region.id === selectedId
        return (
          <button
            key={region.id}
            type="button"
            tabIndex={-1}
            aria-label={region.label}
            title={region.label}
            onClick={() => onSelect(region.id)}
            className={cn(
              'group absolute size-6 -translate-x-1/2 -translate-y-1/2 transition-transform active:scale-90',
              selected ? 'z-20' : 'z-10 hover:z-20'
            )}
            style={{ left: `${(x / 360) * 100}%`, top: `${(y / SPAN_Y) * 100}%` }}
          >
            <svg viewBox="0 0 24 24" className="h-full w-full overflow-visible">
              {selected && on && <circle cx="12" cy="12" r="7" fill="none" strokeWidth="1.5" className="lab-ping stroke-accent" />}
              <circle
                cx="12"
                cy="12"
                r={selected ? 5.5 : 4}
                strokeWidth="1.5"
                className={cn(
                  'transition-all duration-300',
                  selected
                    ? 'fill-accent stroke-background'
                    : lit
                      ? 'fill-accent/80 stroke-foreground group-hover:fill-accent'
                      : 'fill-background/15 stroke-background/30 group-hover:fill-background/40'
                )}
                style={lit || selected ? { filter: 'drop-shadow(0 0 3px var(--accent))' } : undefined}
              />
            </svg>
          </button>
        )
      })}
    </div>
  )
}
